import { LeftOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Card, Divider, Flex, Spin, Table } from 'antd'
import { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import {
  useInvestigationsServiceGetInvestigationTestEndpoint,
  useInvestigationsServiceGetInvestigationTestsEndpointKey,
  useInvestigationsServiceSubmitInvestigationTestToValidationEndpoint
} from 'src/api/queries'
import { ApiError, InvestigationTestStatus, QrCodeType } from 'src/api/requests'
import { VALIDATE_RESULT_PERMISSIONS } from 'src/app-constants'
import {
  ApproveTestModal,
  message,
  PageBreadcrumbs,
  QRCode,
  SaveConfirm,
  Text
} from 'src/components'
import { ResultActions } from 'src/features/persons/components/PersonInvestigations/ResultActions'
import { getFullName } from 'src/features/users/utils'
import { useUser } from 'src/hooks'
import { dayMonthYearHourMinutesFormatWithDot } from 'src/libs'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

import { RejectTestResultModal, TestDetailsFields } from '../components'
import { useSuppliesColumns } from '../hooks'

import { useStyles } from './styles'

export const TestDetailsPage = () => {
  const { id } = useParams()
  const navigate = useNavigate()

  const { t, currentLang } = useLocales()
  const { styles } = useStyles()

  const { hasPermission } = useUser()

  const [isOpenRejectModal, setIsOpenRejectModal] = useState(false)
  const [isOpenApproveModal, setIsOpenApproveModal] = useState(false)

  const queryClient = useQueryClient()
  const { columns } = useSuppliesColumns()

  const hasApprovePermission = hasPermission(VALIDATE_RESULT_PERMISSIONS)

  const { data: test, isLoading: isLoadingTest } =
    useInvestigationsServiceGetInvestigationTestEndpoint(
      { id: String(id) },
      [id],
      { enabled: !!id }
    )

  const { mutate: sendToValidation } =
    useInvestigationsServiceSubmitInvestigationTestToValidationEndpoint({
      onSuccess: () => {
        message.success(t('investigations.successfully_send_to_validation'))
        queryClient.invalidateQueries({
          queryKey: [useInvestigationsServiceGetInvestigationTestsEndpointKey]
        })
      },
      onError: (err: ApiError) => formErrorHandler(err.body.errors)
    })

  const testTitle = test?.code
    ? `${t('analysis.add_analysis.test_number')} - ${test.code}`
    : ''

  const breadcrumbItems = [{ title: t('menu.tests') }, { title: testTitle }]

  const resultColumns: ColumnsType = [
    {
      title: t('laboratory_classifiers.investigation_infection'),
      key: 'infection',
      render: (_, { testType }) => testType?.translation?.[currentLang?.value]
    },
    {
      title: t('tests_results.result'),
      key: 'result',
      render: (_, { resultOption }) =>
        resultOption?.translation?.[currentLang?.value]
    },
    {
      key: 'result value',
      title: t('investigations.result_value'),
      render: (_, { resultValueOption }) =>
        resultValueOption?.translation?.[currentLang?.value]
    },
    {
      key: 'result numeric value',
      title: t('investigations.result_numeric_value'),
      render: (_, { resultValue }) => resultValue
    },
    {
      key: 'result date',
      title: t('tests_results.result_date'),
      render: (_, { resultDate }) =>
        dayjs.utc(resultDate).tz().format(dayMonthYearHourMinutesFormatWithDot)
    },
    {
      key: 'actions',
      render: (_, result) => (
        <ResultActions
          result={result}
          isApproved={test?.isApproved}
          referralDoctorUserId={test?.referralDoctorUser?.id}
          referralDoctorName={test?.referralDoctorName}
          referralDoctorUserName={getFullName(
            test?.referralDoctorUser?.firstName,
            test?.referralDoctorUser?.lastName
          )}
        />
      )
    }
  ]

  const onBackClick = () => navigate(-1)

  const onToggle = () => setIsOpenRejectModal((prev) => !prev)

  return (
    <Spin spinning={isLoadingTest}>
      <PageBreadcrumbs
        title={testTitle}
        items={breadcrumbItems}
        extra={
          <Flex className={styles.qrWrapper}>
            <QRCode content={id} type={QrCodeType.LABORATORY_TEST} />
          </Flex>
        }
        extraProps={{ align: 'flex-start' }}
      >
        <Flex vertical gap={24}>
          <Flex justify="space-between">
            <Button onClick={onBackClick} icon={<LeftOutlined />}>
              {t('common.back')}
            </Button>

            {test?.status === InvestigationTestStatus.SUBMITTED_TO_VALIDATION &&
              hasApprovePermission && (
                <Flex gap={8}>
                  <Button danger type="primary" onClick={onToggle}>
                    {t('investigations.reject_test')}
                  </Button>

                  {/* The doctor confirms or replaces each result in the modal
                      before the test is sent on. */}
                  <Button
                    type="primary"
                    onClick={() => setIsOpenApproveModal(true)}
                  >
                    {t('analysis.add_analysis.send_test_result')}
                  </Button>
                </Flex>
              )}

            {!test?.isApproved &&
              test?.results?.length !== 0 &&
              test?.status === InvestigationTestStatus.NEW && (
                <SaveConfirm
                  title={`${t('investigations.send_to_validation')}?`}
                  placement="leftBottom"
                  onConfirm={() => sendToValidation({ id: String(id) })}
                >
                  <Button type="primary">
                    {t('investigations.send_to_validation')}
                  </Button>
                </SaveConfirm>
              )}
          </Flex>

          <Card>
            <Flex vertical gap={24}>
              <Card bordered>{test && <TestDetailsFields test={test} />}</Card>

              {test?.supplies && (
                <Table
                  rowKey={(record) => String(record.product?.id)}
                  bordered
                  dataSource={test?.supplies}
                  columns={columns}
                  pagination={false}
                  scroll={{ x: 'max-content' }}
                />
              )}

              {test?.results && (
                <>
                  <Divider className={styles.resultDivider} />

                  <Text>{t('tests_results.result')}</Text>

                  <Table
                    bordered
                    pagination={false}
                    dataSource={test?.results}
                    rowKey={(record) => JSON.stringify(record)}
                    columns={resultColumns}
                    scroll={{ x: 'max-content' }}
                  />
                </>
              )}
            </Flex>
          </Card>
        </Flex>
      </PageBreadcrumbs>

      {isOpenRejectModal && (
        <RejectTestResultModal
          id={String(id)}
          onCancel={() => setIsOpenRejectModal(false)}
        />
      )}

      {isOpenApproveModal && (
        <ApproveTestModal
          testId={String(id)}
          results={test?.results}
          onCancel={() => setIsOpenApproveModal(false)}
        />
      )}
    </Spin>
  )
}
